import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { createExport } from '../services/export.service';
import { useNotifyMessage } from './useNotifyMessage';
import { CreateExportRequest } from '../types/Exports';
import { FilterableTypes, FilterableType } from '../types/FilterableTypes';

const DOWNLOADS_ROUTE = '/downloads';

const GENERIC_FILTER_TYPE: Record<FilterableType, number> = {
  [FilterableTypes.Equals]: 0,
  [FilterableTypes.Contains]: 1,
  [FilterableTypes.StartsWith]: 2,
  [FilterableTypes.EndsWith]: 3,
  [FilterableTypes.LessThan]: 4,
  [FilterableTypes.LessThanOrEqual]: 5,
  [FilterableTypes.GreaterThan]: 6,
  [FilterableTypes.GreaterThanOrEqual]: 7,
  [FilterableTypes.Null]: 8,
  [FilterableTypes.NotNull]: 9,
};

const toGenericFilterType = (filterType: FilterableType): number => {
  const mapped = GENERIC_FILTER_TYPE[filterType];
  if (mapped === undefined) {
    throw new Error(`Unmapped export filterType "${String(filterType)}". Add it to GENERIC_FILTER_TYPE in useExport.tsx.`);
  }
  return mapped;
};

/**
 * The API's "fieldValue" is a STRING. Booleans / numbers are stringified.
 * Equals filter values are now sent as-is (no surrounding quotes).
 */
const toFieldValue = (value: any): string | null => {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' ? value : String(value);
};

// Keys that are structural, not the actual field being filtered on.
const META_KEYS = new Set(['filterType', 'and', 'or', 'fieldName', 'fieldValue']);

// SearchBar "search all" emits a bare { <field>: value } with no filterType,
// relying on the backend's default operator. For a search box that default is a
// substring match, so we map those to Contains. If your list actually does
// exact matching, switch this to FilterableTypes.Equals.
const DEFAULT_FILTER_TYPE: FilterableType = FilterableTypes.Contains;

/**
 * Converts an on-screen (search-bar style) filter into the shape the export
 * API expects: { fieldName, filterType, fieldValue, and?, or? }.
 *
 * Handles both shapes defensively:
 *  - already-export-shaped:  { fieldName, filterType, fieldValue }
 *  - dynamic-key shape:      { <field>: value, filterType, and?, or? }
 */
const toExportFilter = (filter: any): any => {
  const { and, or } = filter;
  const filterType: FilterableType = filter.filterType ?? DEFAULT_FILTER_TYPE;

  let fieldName: string;
  let rawValue: any;

  if ('fieldName' in filter && 'fieldValue' in filter) {
    fieldName = filter.fieldName;
    rawValue = filter.fieldValue;
  } else {
    // The field is the first key that is not a structural/meta key.
    fieldName = Object.keys(filter).find((k) => !META_KEYS.has(k)) as string;
    rawValue = filter[fieldName];
  }

  if (!fieldName) {
    throw new Error(`Could not determine fieldName for export filter: ${JSON.stringify(filter)}`);
  }

  const mapped: any = {
    fieldName,
    filterType: toGenericFilterType(filterType),
    fieldValue: toFieldValue(rawValue),
  };

  if (and) mapped.and = toExportFilter(and);
  if (or) mapped.or = toExportFilter(or);

  return mapped;
};

/**
 * Drives the async export job flow for a given entity type.
 * Use one instance per page (entityType is fixed per page, e.g. 'Device').
 */
export function useExport(entityType: string) {
  const { notifyMessage } = useNotifyMessage();
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);

  const startExport = async (filters: any[] = [], sort: any[] = []): Promise<void> => {
    setIsExporting(true);

    try {
      const payload: CreateExportRequest = {
        entityType,
        filter: {
          filters: filters.map(toExportFilter),
          sort,
        },
      };

      await createExport(payload);

      notifyMessage(
        <span>
          Export started. It will be available in{' '}
          <a
            role='button'
            onClick={(e) => {
              e.preventDefault();
              navigate(DOWNLOADS_ROUTE);
            }}
            style={{
              color: 'inherit',
              fontWeight: 600,
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Downloads
          </a>
          .
        </span>,
        'success',
      );
    } catch (error: any) {
      // Surfaces the true cause: either a client-side transform throw, or the
      // backend 400 body (error.response.data) that was previously swallowed.
      // eslint-disable-next-line no-console
      console.error('Export failed:', error?.response?.data ?? error?.message ?? error);
      notifyMessage('Could not start the export. Please try again.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return { isExporting, startExport };
}
